import React, { useMemo, forwardRef, useImperativeHandle } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import StatsCard from '@/components/school-management/StatsCard/StatsCard';
import common from '@/school-management/utils/common';
import { SchoolStatistics as SchoolStatisticsType } from '@/school-management/types/response/adminManagement';
import styles from './styles.module.scss';
import statsGroupStyles from '@/components/school-management/StatsCard/StatsCardGroup.module.scss';
import { useUserStatistics } from '@/school-management/hooks/useUserStatistics';
import { formatMinutes } from '@/utils/formatters';
import { buildUserStatisticsPayload } from '@/school-management/utils/userStatisticsFilters';
import userUtils from '@/utils/user';

export interface SchoolStatisticsRef {
  refetch: () => void;
}

interface SchoolStatisticsProps {
  /** Used only for Google users limit card; 4 main stats come from metrics/user-statistics */
  statistics?: SchoolStatisticsType | null;
  user: any;
  schoolId?: string;
  activeTab?: string;
  teacherFilters?: Record<string, any>;
  classFilters?: Record<string, any>;
  studentFilters?: Record<string, any>;
  hasActiveTabStatsFilters?: boolean;
}

const calculatePercentage = (value: number, total: number): string => {
  if (total <= 0 || value <= 0) return '';
  return `${Math.floor((value / total) * 100)}%`;
};

const SchoolStatistics = forwardRef<SchoolStatisticsRef, SchoolStatisticsProps>(
  (
    {
      statistics,
      user,
      schoolId,
      activeTab,
      teacherFilters,
      classFilters,
      studentFilters,
      hasActiveTabStatsFilters,
    },
    ref
  ) => {
    const { t } = useAdminTranslation();
    const isGoogle = common.isGoogle(user);
    const isAdmin = userUtils.isAdmin(user);

    const statsPayload = useMemo(
      () =>
        buildUserStatisticsPayload({
          activeTab,
          studentFilters,
          teacherFilters,
          classFilters,
          t,
        }),
      [activeTab, studentFilters, teacherFilters, classFilters, t]
    );

    const {
      data: userStats,
      loading: statsLoading,
      error: statsError,
      refetch,
    } = useUserStatistics(!!schoolId, statsPayload);

    useImperativeHandle(
      ref,
      () => ({
        refetch: () => {
          refetch();
        },
      }),
      [refetch]
    );

    const hasRegisteredUsers = (userStats?.registeredStudents ?? 0) > 0;

    const hasNoDataForFilters =
      !!hasActiveTabStatsFilters && !statsLoading && (userStats?.registeredStudents ?? 0) === 0;

    const statsFilterEmptyMessage = useMemo(() => {
      switch (activeTab) {
        case 'teachers':
          return t('schoolDetailsPage.teachers.zeroState.filterTitle') || '';
        case 'classes':
          return t('schoolDetailsPage.classes.zeroState.filterTitle') || '';
        case 'students':
        default:
          return t('schoolDetailsPage.students.zeroState.filterTitle') || '';
      }
    }, [activeTab, t]);

    const emptyMessageForCards = hasNoDataForFilters ? statsFilterEmptyMessage : null;

    return (
      <div className={styles.statsSection}>
        <div className={styles.statsHeader}>
          {
            //TODO: Add fetch time here
          }
        </div>

        <div className={statsGroupStyles.statsGroup}>
          {isGoogle && statistics && (
            <StatsCard
              value={statistics.usersLimit.toLocaleString()}
              label={t('statsCardGroup.usersLimit')}
              tooltip={t('statsCardGroup.usersLimitTooltip') || ''}
              emptyMessage={emptyMessageForCards ?? (t('statsCardGroup.usersLimitEmpty') || '')}
            />
          )}

          {isAdmin && (
            <>
              <StatsCard
                value={userStats?.engagedTeachers ?? 0}
                label={t('statsCardGroup.engagedTeachers')}
                tooltip={t('statsCardGroup.engagedTeachersTooltip') || ''}
                emptyMessage={
                  emptyMessageForCards ?? (t('statsCardGroup.engagedTeachersEmpty') || '')
                }
                isLoading={statsLoading}
              />
            </>
          )}

          {!isAdmin && (
            <>
              <StatsCard
                value={userStats?.passiveStudents ?? 0}
                label={t('statsCardGroup.passiveStudents')}
                tooltip={t('statsCardGroup.passiveStudentsTooltip') || ''}
                emptyMessage={
                  emptyMessageForCards ?? (t('statsCardGroup.passiveStudentsEmpty') || '')
                }
                disableFallback={hasRegisteredUsers}
                isLoading={statsLoading}
              />
            </>
          )}

          <StatsCard
            value={userStats?.engagedStudents ?? 0}
            label={t('statsCardGroup.engagedStudents')}
            tooltip={t('statsCardGroup.engagedStudentsTooltip') || ''}
            emptyMessage={emptyMessageForCards ?? (t('statsCardGroup.engagedStudentsEmpty') || '')}
            disableFallback={hasRegisteredUsers}
            isLoading={statsLoading}
          />

          <StatsCard
            value={
              hasNoDataForFilters
                ? '0m 0s'
                : userStats != null && typeof userStats.totalTimeSpent === 'number'
                  ? formatMinutes(userStats.totalTimeSpent)
                  : '0m 0s'
            }
            label={t('statsCardGroup.totalTimeSpent')}
            tooltip={t('statsCardGroup.totalTimeSpentTooltip') || ''}
            isTimeValue={true}
            isTimeCard={true}
            labelIcon='clock'
            emptyMessage={emptyMessageForCards ?? (t('statsCardGroup.totalTimeSpentEmpty') || '')}
            disableFallback={hasRegisteredUsers}
            isLoading={statsLoading}
          />

          <StatsCard
            value={
              hasNoDataForFilters
                ? '0m 0s'
                : userStats != null && typeof userStats.averageTimeSpent === 'number'
                  ? formatMinutes(userStats.averageTimeSpent)
                  : '0m 0s'
            }
            label={t('statsCardGroup.averageSessionTime')}
            tooltip={t('statsCardGroup.averageSessionTimeTooltip') || ''}
            isTimeValue={true}
            isTimeCard={true}
            emptyMessage={
              emptyMessageForCards ?? (t('statsCardGroup.averageSessionTimeEmpty') || '')
            }
            disableFallback={hasRegisteredUsers}
            isLoading={statsLoading}
          />
        </div>
      </div>
    );
  }
);

export default SchoolStatistics;
